import React, { useState, useRef, useEffect } from 'react';
import './ClinicChatView.css';
import PatientSidebar from './PatientSidebar';
import { getClinicId } from '../../utils/config';
import FormatUrl from '../../utils/UrlFormatter';
// import SendTestMessage from './SendTestMessage';

const ClinicChatView = ({ user }) => {
  const clinicId = getClinicId(user);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sidebarWidth, setSidebarWidth] = useState(250);
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async (clinicId, patientProfileId) => {
    try {
      const csrfUrl = FormatUrl(`/csrf-token/`);
      const csrfRes = await fetch(csrfUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const { csrfToken } = await csrfRes.json();

      const url = FormatUrl(`/conv/clinics/${clinicId}/conversations/patient/${patientProfileId}`);
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });

      if (!res.ok) throw new Error('Failed to fetch messages');
      const data = await res.json();
      const messageRecords = data.records || [];

      const formattedMessages = messageRecords.map((msg) => ({
        id: msg.id,
        sender:
          msg.initiator === 'clinic' && msg.direction === 'outbound'
            ? 'clinic'
            : 'patient',
        text: msg.message,
        timestamp: new Date(msg.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        channel: msg.channel,
      }));

      setMessages(formattedMessages.reverse());
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const sendMessageToPatient = async () => {
    if (!selectedPatient || input.trim() === '') return;
    try {
      const csrfUrl = FormatUrl(`/csrf-token/`);
      const csrfRes = await fetch(csrfUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const { csrfToken } = await csrfRes.json();

      const url = FormatUrl(`/conv/send-comms-patient`);
      const payload = {
        clinic_id: clinicId,
        profile_id: selectedPatient.patient_profile_id,
        message: input,
        subject: 'Chat Message',
      };

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to send message');

      const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setMessages([...messages, { id: Date.now(), sender: 'clinic', text: input, timestamp, channel: 'in_app' }]);
      setInput('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleSelectPatient = (patientObj) => {
    setSelectedPatient(patientObj);
    fetchMessages(clinicId, patientObj.patient_profile_id);
  };

  const handleBackToSidebar = () => {
    setSelectedPatient(null);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging || isSidebarCollapsed) return;
      const newWidth = e.clientX;
      if (newWidth > 180 && newWidth < 400) {
        setSidebarWidth(newWidth);
      }
    };

    const onMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, isSidebarCollapsed]);

  return (

    <>
    {/* <SendTestMessage/> */}
    
    <div className={`clinic-chat-wrapper ${isFullscreen ? 'fullscreen' : ''}`}>
      {!isMobileView && (
        <div className="fullscreen-toggle">
          <button onClick={() => setIsFullscreen(!isFullscreen)}>
            {isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          </button>
        </div>
      )}

      <div className="clinic-chat-page">
        {(!isMobileView || !selectedPatient) && !isSidebarCollapsed && (
          <div
            className="chat-sidebar"
            style={{ width: isMobileView ? '100%' : sidebarWidth }}
          >
            <PatientSidebar
              clinicId={clinicId}
              selectedPatientId={selectedPatient?.patient_profile_id}
              onSelectPatient={handleSelectPatient}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onCollapseSidebar={() => setIsSidebarCollapsed(true)}
            />
          </div>
        )}

        {!isSidebarCollapsed && !isMobileView && (
          <div className="sidebar-resizer" onMouseDown={handleMouseDown} />
        )}

        {isSidebarCollapsed && !isMobileView && (
          <div className="collapsed-toggle">
            <button onClick={() => setIsSidebarCollapsed(false)}>❯</button>
          </div>
        )}

        {(!isMobileView || selectedPatient) && (
          <div className={`chat-main ${isMobileView && selectedPatient ? 'active' : ''}`}>
            {isMobileView && selectedPatient && (
              <div className="chat-header">
                <button onClick={handleBackToSidebar}>←</button>
                Communicate with ({selectedPatient.first_name} {selectedPatient.last_name})
              </div>
            )}

            {!isMobileView && (
              <div className="chat-header">
                Communicate  {selectedPatient ? `(${selectedPatient.first_name} ${selectedPatient.last_name})` : ''}
              </div>
            )}

            {selectedPatient ? (
              <>
                <div className="chat-body">
                  {messages.map((msg, idx) => (
                    <div key={idx} className={`chat-msg ${msg.sender === 'clinic' ? 'clinic' : 'patient'}`}>
                      <div className="chat-bubble">
                        <span className="chat-text">{msg.text}</span>
                        <span className="chat-time">{msg.timestamp}</span>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>

                <div className="chat-input-bar">
                  <input
                    type="text"
                    value={input}
                    placeholder="Type a message..."
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendMessageToPatient()}
                  />
                  <button onClick={sendMessageToPatient}>Send</button>
                </div>
              </>
            ) : (
              <div className="welcome-message">
                <h3>Welcome to the Communication Console</h3>
                <p>
                  Here you can communicate directly with your patients. To get started, select a patient
                  from the sidebar. You’ll see their message history and can chat with them in real-time.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ClinicChatView;
